import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Typography,
  TextField,
  Autocomplete
} from '@mui/material';

import { LoadingButton } from '@mui/lab';

import request from '../../utils/request';
import SnackbarAlert from '../common/SnackbarAlert';

function CreatEdit({
  closeCreateEditForm,
  stateEditDatas,
  editStatus
}) {
  const [loader, setLoader] = useState(false);

  const [showSnackBarAlert, setShowSnackBarAlert] = useState({
    status: false,
    type: 'success',
    message: null
  });

  const moduleOptions = [
    'RFQ',
    'PlacementSlip',
    'Policy',
    'Claims',
    'Endorsement'
  ];

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Document Name is required')
      .min(2, 'Document Name must be at least 2 characters')
      .max(100, 'Document Name must not exceed 100 characters')
      .matches(
        /^[^0-9].*$/,
        'Document Name cannot start with a number'
      ),

    modules: Yup.array()
      .min(1, 'Please select at least one Module')
      .required('Modules are required')
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: stateEditDatas?.id || '',
      name: stateEditDatas?.name || '',
      modules: stateEditDatas?.modules || []
    },

    validationSchema,

    onSubmit: async (values) => {
      try {
        setLoader(true);

        const payload = editStatus
          ? {
            id: values.id,
            name: values.name.trim(),
            modules: values.modules
          }
          : {
            name: values.name.trim(),
            modules: values.modules
          };

        const response = await request({
          url: editStatus
            ? 'documentName/update'
            : 'documentName/create',
          method: editStatus ? 'PUT' : 'POST',
          data: payload
        });

        if (response.status) {
          closeCreateEditForm(true);
        } else {
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: response?.['message'] || 'Something went wrong'
          });
        }
      } catch (error) {
        setShowSnackBarAlert({
          status: true,
          type: 'error',
          message: error?.message || 'Something went wrong'
        });
      } finally {
        setLoader(false);
      }
    }
  });

  const handleSnackbarClose = () => {
    setShowSnackBarAlert({
      status: false,
      type: '',
      message: ''
    });
  };

  return (
    <>
      <SnackbarAlert
        showSnackBarAlert={showSnackBarAlert}
        handleSnackbarClose={handleSnackbarClose}
      />

      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}  style={{ minHeight: '81vh' }}>
            <Card>
              <CardHeader
                title={
                  editStatus
                    ? 'Update Document Name'
                    : 'Create Document Name'
                }
              />

              <Divider />

              <CardContent>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        name="name"
                        label={
                          <>
                            Document Name{' '}
                            <Typography
                              component="span"
                              sx={{ color: 'red' }}
                            >
                              *
                            </Typography>
                          </>
                        }
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          formik.setFieldValue(
                            'name',
                            e.target.value.trim()
                          );
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.name &&
                          Boolean(formik.errors.name)
                        }
                        helperText={
                          formik.touched.name &&
                          formik.errors.name
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        options={moduleOptions}
                        value={formik.values.modules}
                        onChange={(event, value) => {
                          formik.setFieldValue(
                            'modules',
                            value
                          );
                        }}
                        onBlur={() =>
                          formik.setFieldTouched(
                            'modules',
                            true
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <>
                                Modules{' '}
                                <Typography
                                  component="span"
                                  sx={{ color: 'red' }}
                                >
                                  *
                                </Typography>
                              </>
                            }
                            size="small"
                            error={
                              formik.touched.modules &&
                              Boolean(
                                formik.errors.modules
                              )
                            }
                            helperText={
                              formik.touched.modules &&
                              formik.errors.modules
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mt: 2
                      }}
                    >
                      {loader ? (
                        <LoadingButton
                          loading
                          variant="contained"
                        >
                          Save
                        </LoadingButton>
                      ) : (
                        <Button
                          type="submit"
                          variant="contained"
                        >
                          Save
                        </Button>
                      )}

                      <Button
                        variant="contained"
                        color="error"
                        onClick={() =>
                          closeCreateEditForm(false)
                        }
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default CreatEdit;